import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  ButtonBase,
  Button,
  } from '@mui/material';
import {
  Close,
  Tour,
  RocketLaunch,
  Explore,
  Architecture,
  Chat,
  VerifiedUser,
  ArrowForward,
  ArrowBack,
  PlayArrow,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useTour } from '../help/TourProvider';
import { api } from '../../services/api';

const VALUE_SLIDES = [
  {
    icon: <Architecture sx={{ fontSize: 48 }} />,
    title: 'Engineering Intelligence',
    headline: 'Your CAD files, documents, and requirements — all in one place',
    points: [
      'Upload and view 2D/3D CAD files directly in the browser',
      'AI-powered drawing generation and modification',
      'Version control built for engineering workflows',
    ],
    color: '#667eea',
  },
  {
    icon: <Chat sx={{ fontSize: 48 }} />,
    title: 'AI-Powered Assistant',
    headline: 'e-pilot understands your engineering context',
    points: [
      'Ask questions about your project data in natural language',
      'Generate documents, extract requirements from specs',
      'Context-aware — e-pilot reads your uploaded files',
    ],
    color: '#764ba2',
  },
  {
    icon: <VerifiedUser sx={{ fontSize: 48 }} />,
    title: 'Compliance & Quality',
    headline: 'Automate compliance checking across your designs',
    points: [
      'Check CAD drawings against requirements and standards',
      'Track project health with scorecards and analytics',
      'Build automated engineering tool pipelines',
    ],
    color: '#11998e',
  },
];

export default function WelcomeDialog({ onCreateProject }) {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0); // 0-2 = value slides, 3 = action picker
  const { user } = useAuth();
  const { startTour } = useTour();
  const checkedRef = React.useRef(false);

  useEffect(() => {
    if (!user) return;
    if (checkedRef.current) return;
    checkedRef.current = true;

    // localStorage is the PRIMARY guard — fast, reliable, no network dependency
    if (localStorage.getItem('eis_welcome_seen')) return;

    // Secondary: check backend (handles cross-device, cleared localStorage)
    let cancelled = false;
    api.getUserProfile().then(res => {
      if (cancelled) return;
      const profile = res.data;
      const alreadySeen = profile.onboarding_completed ||
        (profile.onboarding_progress && Object.keys(profile.onboarding_progress).length > 0);
      if (alreadySeen) {
        localStorage.setItem('eis_welcome_seen', '1');
        return;
      }
      setTimeout(() => { if (!cancelled) setOpen(true); }, 1500);
    }).catch(() => {
      // Backend unavailable (deploy in progress, 500, etc.)
      // Still show dialog for fresh users — they'll dismiss it and localStorage will persist
    });
    return () => { cancelled = true; };
  }, [user]);

  const markWelcomeSeen = () => {
    // 1. localStorage — instant, survives page refresh
    localStorage.setItem('eis_welcome_seen', '1');
    // 2. Backend — persists across devices/browsers (fire-and-forget with retry)
    const persistToBackend = () => {
      Promise.all([
        api.updateOnboardingProgress({ welcomed: true }),
        api.completeOnboarding(),
      ]).catch(() => {
        // Retry once after 5s (handles deploy-in-progress 500s)
        setTimeout(() => {
          api.completeOnboarding().catch(err => console.warn('Onboarding completion retry failed:', err?.message || err));
        }, 5000);
      });
    };
    persistToBackend();
  };

  const handleTour = () => {
    setOpen(false);
    markWelcomeSeen();
    setTimeout(() => startTour('welcome'), 300);
  };

  const handleCreateProject = () => {
    setOpen(false);
    markWelcomeSeen();
    if (onCreateProject) onCreateProject();
  };

  const handleExplore = () => {
    setOpen(false);
    markWelcomeSeen();
  };

  const isLastSlide = activeStep === VALUE_SLIDES.length - 1;
  const isActionStep = activeStep === VALUE_SLIDES.length;
  const currentSlide = VALUE_SLIDES[activeStep];

  const options = [
    {
      icon: <Tour sx={{ fontSize: 32 }} />,
      title: 'Take a Quick Tour',
      description: 'Guided walkthrough of the platform\'s key features',
      onClick: handleTour,
    },
    {
      icon: <RocketLaunch sx={{ fontSize: 32 }} />,
      title: 'Create Your First Project',
      description: 'Jump right in and set up your first project',
      onClick: handleCreateProject,
    },
    {
      icon: <Explore sx={{ fontSize: 32 }} />,
      title: 'Explore on My Own',
      description: 'Use the Getting Started checklist at your own pace',
      onClick: handleExplore,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleExplore}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Gradient header */}
      <Box
        sx={{
          background: isActionStep
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : `linear-gradient(135deg, ${currentSlide.color} 0%, ${currentSlide.color}dd 100%)`,
          px: 3,
          py: 3,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          transition: 'background 0.4s',
          minHeight: isActionStep ? 'auto' : 180,
        }}
      >
        <Box sx={{ flex: 1 }}>
          {isActionStep ? (
            <>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                Ready to Get Started?
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                {user?.username ? `${user.username}, h` : 'H'}ow would you like to begin?
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{ color: 'rgba(255,255,255,0.9)', mb: 1.5 }}>
                {currentSlide.icon}
              </Box>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: 1.5 }}>
                {currentSlide.title}
              </Typography>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.3 }}>
                {currentSlide.headline}
              </Typography>
            </>
          )}
        </Box>
        <IconButton onClick={handleExplore} sx={{ color: 'white', mt: -0.5 }}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {isActionStep ? (
          /* Action picker — same as before but refined */
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {options.map((option) => (
              <ButtonBase
                key={option.title}
                onClick={option.onClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'left',
                  width: '100%',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#667eea',
                    bgcolor: 'rgba(102, 126, 234, 0.04)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: 'rgba(102, 126, 234, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#667eea',
                    flexShrink: 0,
                  }}
                >
                  {option.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {option.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {option.description}
                  </Typography>
                </Box>
              </ButtonBase>
            ))}
          </Box>
        ) : (
          /* Value slide content */
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {currentSlide.points.map((point, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: `${currentSlide.color}15`,
                      color: currentSlide.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {point}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Footer with navigation */}
        <Box sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          {/* Step indicator */}
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {[...VALUE_SLIDES, { title: 'Get Started' }].map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: activeStep === i ? 20 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: activeStep === i ? '#667eea' : 'rgba(102, 126, 234, 0.2)',
                  transition: 'all 0.3s',
                  cursor: 'pointer',
                }}
                onClick={() => setActiveStep(i)}
              />
            ))}
          </Box>

          {/* Navigation buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeStep > 0 && (
              <Button
                size="small"
                startIcon={<ArrowBack sx={{ fontSize: 14 }} />}
                onClick={() => setActiveStep(prev => prev - 1)}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                Back
              </Button>
            )}
            {!isActionStep && (
              <>
                <Button
                  size="small"
                  onClick={() => setActiveStep(VALUE_SLIDES.length)}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Skip
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  endIcon={isLastSlide ? <PlayArrow sx={{ fontSize: 14 }} /> : <ArrowForward sx={{ fontSize: 14 }} />}
                  onClick={() => setActiveStep(prev => prev + 1)}
                  sx={{
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${currentSlide.color} 0%, ${currentSlide.color}dd 100%)`,
                    '&:hover': { opacity: 0.9 },
                  }}
                >
                  {isLastSlide ? 'Get Started' : 'Next'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
